/**
 * Generated by Kubb (https://kubb.dev/).
 * Do not edit manually.
 */

import type { RequestConfig, ResponseErrorConfig } from '../.kubb/client'
import type {
  GetOrganicTrafficConfigOptions,
  GetOrganicTrafficConfigStatus200,
  GetOrganicTrafficConfigStatus403,
  GetOrganicTrafficConfigStatus404,
  GetOrganicTrafficConfigStatus500,
} from '../types/GetOrganicTrafficConfig'
import type {
  QueryKey,
  QueryClient,
  QueryObserverOptions,
  UseQueryResult,
} from '@tanstack/react-query'
import { getOrganicTrafficConfig } from '../clients/getOrganicTrafficConfig'
import { queryOptions, useQuery } from '@tanstack/react-query'

export const getOrganicTrafficConfigQueryKey = ({
  path,
}: Omit<GetOrganicTrafficConfigOptions, 'headers'>) =>
  [{ url: '/v1/bots/organic-traffic/config/:order_book_id', params: path }] as const

type GetOrganicTrafficConfigQueryKey = ReturnType<typeof getOrganicTrafficConfigQueryKey>

export function getOrganicTrafficConfigQueryOptions(
  { path }: GetOrganicTrafficConfigOptions,
  config: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>> = {},
) {
  const queryKey = getOrganicTrafficConfigQueryKey({ path })
  return queryOptions<
    GetOrganicTrafficConfigStatus200,
    ResponseErrorConfig<
      | GetOrganicTrafficConfigStatus403
      | GetOrganicTrafficConfigStatus404
      | GetOrganicTrafficConfigStatus500
    >,
    GetOrganicTrafficConfigStatus200,
    typeof queryKey
  >({
    queryKey,
    queryFn: async ({ signal }) => {
      return getOrganicTrafficConfig({
        ...config,
        path,
        signal: config.signal ?? signal,
        throwOnError: true,
      }).unwrap()
    },
  })
}

/**
 * @description Admin only. Returns 404 when the order book has no stored configuration, in which case it is scored with the bot service's own configuration.
 * @summary Get an order book's organic activity score configuration
 * {@link /v1/bots/organic-traffic/config/:order_book_id}
 */
export function useGetOrganicTrafficConfig<
  TData = GetOrganicTrafficConfigStatus200,
  TQueryData = GetOrganicTrafficConfigStatus200,
  TQueryKey extends QueryKey = GetOrganicTrafficConfigQueryKey,
>(
  {
    path,
  }: {
    path: GetOrganicTrafficConfigOptions['path'] | (() => GetOrganicTrafficConfigOptions['path'])
  },
  options: {
    query?: Partial<
      QueryObserverOptions<
        GetOrganicTrafficConfigStatus200,
        ResponseErrorConfig<
          | GetOrganicTrafficConfigStatus403
          | GetOrganicTrafficConfigStatus404
          | GetOrganicTrafficConfigStatus500
        >,
        TData,
        TQueryData,
        TQueryKey
      >
    > & { client?: QueryClient }
    client?: Partial<Omit<RequestConfig, 'path' | 'query' | 'body' | 'headers' | 'url'>>
  } = {},
) {
  const { query: queryConfig = {}, client: config = {} } = options ?? {}
  const { client: queryClient, ...resolvedOptions } = queryConfig
  const resolvedParams = { path: typeof path === 'function' ? path() : path }
  const queryKey = resolvedOptions?.queryKey ?? getOrganicTrafficConfigQueryKey(resolvedParams)

  const queryResult = useQuery(
    {
      ...getOrganicTrafficConfigQueryOptions(resolvedParams, config),
      ...resolvedOptions,
      queryKey,
    } as unknown as QueryObserverOptions,
    queryClient,
  ) as UseQueryResult<
    TData,
    ResponseErrorConfig<
      | GetOrganicTrafficConfigStatus403
      | GetOrganicTrafficConfigStatus404
      | GetOrganicTrafficConfigStatus500
    >
  > & { queryKey: TQueryKey }

  queryResult.queryKey = queryKey as TQueryKey

  return queryResult
}
